import React, { useState, useRef } from 'react';
import { Heart, Eye, Plus, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

// Size configurations for product cards
const SIZE_CONFIG = {
  small: {
    imageClass: 'aspect-square',
    titleClass: 'text-sm line-clamp-1',
    priceClass: 'text-base',
    buttonSize: 'h-8 w-8',
    iconSize: 'w-3.5 h-3.5',
    padding: 'p-2',
  },
  medium: {
    imageClass: 'aspect-square',
    titleClass: 'text-base line-clamp-2',
    priceClass: 'text-lg',
    buttonSize: 'h-10 w-10',
    iconSize: 'w-4 h-4',
    padding: 'p-3',
  },
  large: {
    imageClass: 'aspect-[4/3]',
    titleClass: 'text-lg line-clamp-2',
    priceClass: 'text-xl',
    buttonSize: 'h-12 w-12',
    iconSize: 'w-5 h-5',
    padding: 'p-4',
  },
};

const ProductCard = ({ product, onAddToCart, onToggleFavorite, isFavorite, onViewDetails, cardSize = 'medium' }) => {
  const { t } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const clickTimeoutRef = useRef(null);
  const clickCountRef = useRef(0);
  
  const config = SIZE_CONFIG[cardSize] || SIZE_CONFIG.medium;

  const isOutOfStock = product.inStock === false;
  const isUpdated = product.updated === true;
  const isNewlyImported = product.newly_imported === true;

  // Handle click - single click adds to cart, double click opens details
  const handleClick = (e) => {
    e.preventDefault();
    clickCountRef.current += 1;

    if (clickCountRef.current === 1) {
      clickTimeoutRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 300);
    } else if (clickCountRef.current === 2) {
      clearTimeout(clickTimeoutRef.current);
      clickCountRef.current = 0;
      if (onViewDetails) {
        onViewDetails(product);
      }
    }
  };

  const handleViewClick = (e) => {
    e.stopPropagation();
    if (onViewDetails) {
      onViewDetails(product);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden border-2 border-[#0a1628]/20 hover:border-[#0a1628]/50 shadow-lg hover:shadow-xl hover:shadow-[#0a1628]/10 transition-all duration-300 group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container - Dynamic aspect ratio based on size */}
      <div className={`relative ${config.imageClass} bg-[#0a1628]/5 overflow-hidden`}>
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect fill="%23f3f4f6" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="14">No Image</text></svg>';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <span className="text-gray-400 text-sm">No Image</span>
          </div>
        )}
        
        {/* Favorite Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(product.id);
          }}
          className="absolute top-3 left-3 w-10 h-10 rounded-full bg-white border border-[#0a1628]/30 shadow-lg flex items-center justify-center hover:bg-[#0a1628]/5 hover:scale-110 transition-all duration-200 z-10"
        >
          <Heart
            className={`w-5 h-5 ${
              isFavorite ? 'fill-red-500 text-red-500' : 'text-[#0a1628]/70 hover:text-red-400'
            } transition-colors`}
          />
        </button>

        {/* Region Badge */}
        <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white border border-[#0a1628]/30 shadow-lg flex items-center justify-center text-lg z-10">
          {product.region}
        </div>

        {/* NEW Badge for newly imported products */}
        {isNewlyImported && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg z-10 animate-pulse">
            NEW
          </div>
        )}

        {/* Updated Badge */}
        {isUpdated && (
          <div className="absolute bottom-2 left-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg z-10 animate-pulse">
            <RefreshCw className="w-3 h-3" />
            UPDATED
          </div>
        )}

        {/* Out of Stock overlay */}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <span className="bg-[#0a1628] text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
              {t('outOfStock')}
            </span>
          </div>
        )}

        {/* Hover Overlay */}
        {!isOutOfStock && (
          <div
            className={`absolute inset-0 bg-white/70 flex flex-col items-center justify-center gap-2 transition-all duration-300 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <button 
              onClick={handleViewClick}
              className={`flex items-center gap-2 bg-[#0a1628] text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-[#0a1628]/90 transition-all duration-200 shadow-lg hover:scale-105`}
            >
              <Eye className={config.iconSize} />
              {t('viewDetails')}
            </button>
          </div>
        )}
      </div>

      {/* Content - Dynamic padding and text sizes */}
      <div className={`${config.padding} bg-white`}>
        <h3 className={`font-bold text-[#0a1628] ${config.titleClass}`}>
          {product.name}
        </h3>
        
        <div className="flex items-center justify-between mt-2">
          <span className={`${config.priceClass} font-extrabold text-[#0a1628]`}>
            {product.currencySymbol || '$'}{product.price.toLocaleString(undefined, {minimumFractionDigits: product.currency === 'NGN' ? 0 : 2, maximumFractionDigits: product.currency === 'NGN' ? 0 : 2})}
          </span>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product);
            }}
            disabled={isOutOfStock}
            className={`rounded-full ${config.buttonSize} flex items-center justify-center font-bold shadow-lg hover:scale-105 transition-all duration-200 ${
              isOutOfStock 
                ? 'bg-[#0a1628]/20 text-[#0a1628]/50 cursor-not-allowed' 
                : 'bg-[#0a1628] hover:bg-[#0a1628]/90 text-white hover:shadow-[#0a1628]/20'
            }`}
            data-testid="add-to-cart-btn"
          >
            <Plus className={config.iconSize} strokeWidth={3} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
